/**
 * 🎨 Theme Manager - Gestión de temas visuales
 * @version 1.0.0
 * @description Gestor de temas para el chat widget
 */

import { ThemeStyles } from "./theme-styles.js";

export class ThemeManager {
  constructor() {
    this.currentTheme = "professional";
    this.styleElementId = "modul-assistant-sdk-chat-styles";
  }

  /**
   * 🎨 Obtiene los estilos CSS según el tema seleccionado
   * @param {string} theme - Tema seleccionado
   * @returns {string} Estilos CSS completos
   */
  getThemeStyles(theme = this.currentTheme) {
    const baseStyles = this._getBaseStyles();
    const themeStyles = this._getThemeSpecificStyles(theme);
    return `${baseStyles}\n${themeStyles}`;
  }

  /**
   * 💅 Inyecta estilos CSS del chat
   * @param {string} theme - Tema a aplicar
   */
  injectStyles(theme = this.currentTheme) {
    this.currentTheme = theme;

    // Remover estilos existentes si existen
    const existingStyle = document.getElementById(this.styleElementId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = this.styleElementId;
    style.textContent = this.getThemeStyles(theme);
    document.head.appendChild(style);
  }

  /**
   * 🔄 Cambia el tema del chat
   * @param {string} newTheme - Nuevo tema a aplicar
   */
  changeTheme(newTheme) {
    this.injectStyles(newTheme);
  }

  /**
   * 🗑️ Remueve los estilos CSS
   */
  removeStyles() {
    const styleElement = document.getElementById(this.styleElementId);
    if (styleElement && styleElement.parentNode) {
      styleElement.parentNode.removeChild(styleElement);
    }
  }

  /**
   * 🏗️ Obtiene los estilos base comunes a todos los temas
   * @private
   * @returns {string} Estilos CSS base
   */
  _getBaseStyles() {
    return `
      /* Modul Assistant SDK Chat Styles - Base */
      .modul-assistant-sdk-chat-widget {
        position: fixed;
        bottom: 30px;
        right: 30px;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .modul-assistant-sdk-chat-button {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: modul-assistant-sdk-pulse 2s infinite;
      }

      .modul-assistant-sdk-chat-window {
        position: absolute;
        bottom: 80px;
        right: 0;
        width: 350px;
        height: 500px;
        background: white;
        border-radius: 12px;
        display: none;
        flex-direction: column;
        overflow: hidden;
        transform: translateY(20px);
        opacity: 0;
        transition: all 0.3s ease;
      }

      .modul-assistant-sdk-chat-window.open {
        display: flex;
        transform: translateY(0);
        opacity: 1;
      }

      .modul-assistant-sdk-chat-header {
        color: white;
        padding: 16px 20px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .modul-assistant-sdk-chat-header h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
        color: #f8fafc;
      }

      .modul-assistant-sdk-chat-close {
        background: none;
        border: none;
        color: #cbd5e1;
        font-size: 1.1rem;
        cursor: pointer;
        padding: 6px;
        border-radius: 4px;
        transition: all 0.3s ease;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .modul-assistant-sdk-chat-close:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
        transform: scale(1.1);
      }

      .modul-assistant-sdk-chat-body {
        flex: 1;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow-y: auto;
      }

      .modul-assistant-sdk-chat-message {
        max-width: 80%;
        padding: 12px 16px;
        border-radius: 12px;
        font-size: 0.9rem;
        line-height: 1.5;
        word-wrap: break-word;
      }

      .modul-assistant-sdk-chat-message.bot {
        background: white;
        color: #374151;
        align-self: flex-start;
        border-bottom-left-radius: 4px;
      }

      /* Estilos para contenido Markdown en mensajes del bot */
      .modul-assistant-sdk-chat-message.bot h1,
      .modul-assistant-sdk-chat-message.bot h2,
      .modul-assistant-sdk-chat-message.bot h3 {
        margin: 0.5em 0;
        font-weight: bold;
        line-height: 1.2;
      }

      .modul-assistant-sdk-chat-message.bot h1 { font-size: 1.2em; }
      .modul-assistant-sdk-chat-message.bot h2 { font-size: 1.1em; }
      .modul-assistant-sdk-chat-message.bot h3 { font-size: 1.05em; }

      .modul-assistant-sdk-chat-message.bot p {
        margin: 0.5em 0;
        line-height: 1.4;
      }

      .modul-assistant-sdk-chat-message.bot p:first-child {
        margin-top: 0;
      }

      .modul-assistant-sdk-chat-message.bot p:last-child {
        margin-bottom: 0;
      }

      .modul-assistant-sdk-chat-message.bot strong {
        font-weight: bold;
        color: #1f2937;
      }

      .modul-assistant-sdk-chat-message.bot em {
        font-style: italic;
        color: #4b5563;
      }

      .modul-assistant-sdk-chat-message.bot code {
        background: #f1f5f9;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
        font-size: 0.85em;
        color: #475569;
        border: 1px solid #e2e8f0;
      }

      .modul-assistant-sdk-chat-message.bot pre {
        background: #f1f5f9;
        padding: 12px 16px;
        border-radius: 8px;
        margin: 0.5em 0;
        overflow-x: auto;
        border: 1px solid #e2e8f0;
      }

      .modul-assistant-sdk-chat-message.bot pre code {
        background: none;
        padding: 0;
        border: none;
        color: #475569;
        font-size: 0.8em;
      }

      .modul-assistant-sdk-chat-message.bot ul,
      .modul-assistant-sdk-chat-message.bot ol {
        margin: 0.5em 0;
        padding-left: 1.2em;
      }

      .modul-assistant-sdk-chat-message.bot li {
        margin: 0.2em 0;
        line-height: 1.4;
      }

      .modul-assistant-sdk-chat-message.bot blockquote {
        margin: 0.5em 0;
        padding: 0.5em 0.8em;
        border-left: 3px solid #64748b;
        background: #f8fafc;
        font-style: italic;
        color: #64748b;
      }

      .modul-assistant-sdk-chat-message.bot hr {
        border: none;
        border-top: 1px solid #e2e8f0;
        margin: 0.8em 0;
      }

      .modul-assistant-sdk-chat-message.bot a {
        text-decoration: underline;
        transition: all 0.3s ease;
      }

      .modul-assistant-sdk-chat-message.bot img {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
        margin: 0.5em 0;
      }

      .modul-assistant-sdk-chat-message.user {
        align-self: flex-end;
        border-bottom-right-radius: 4px;
        color: white;
      }

      .modul-assistant-sdk-chat-input-area {
        padding: 16px 20px;
        background: white;
      }

      .modul-assistant-sdk-chat-input-form {
        display: flex;
        gap: 12px;
        align-items: center;
      }

      .modul-assistant-sdk-chat-input {
        flex: 1;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 0.9rem;
        outline: none;
        transition: all 0.3s ease;
        font-family: inherit;
      }

      .modul-assistant-sdk-chat-send {
        width: 42px;
        height: 42px;
        border: none;
        border-radius: 8px;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        font-size: 1rem;
      }

      .modul-assistant-sdk-chat-options {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .modul-assistant-sdk-chat-option {
        background: white;
        color: #374151;
        padding: 12px 16px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 0.9rem;
        text-align: center;
        transition: all 0.3s ease;
        font-family: inherit;
        font-weight: 500;
      }

      .modul-assistant-sdk-typing-indicator {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        background: white;
        border-radius: 12px;
        border-bottom-left-radius: 4px;
        max-width: 80%;
        align-self: flex-start;
      }

      .modul-assistant-sdk-typing-dots {
        display: flex;
        gap: 4px;
      }

      .modul-assistant-sdk-typing-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        animation: modul-assistant-sdk-typing 1.4s infinite ease-in-out;
      }

      .modul-assistant-sdk-typing-dot:nth-child(1) { animation-delay: -0.32s; }
      .modul-assistant-sdk-typing-dot:nth-child(2) { animation-delay: -0.16s; }

      @keyframes modul-assistant-sdk-pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      @keyframes modul-assistant-sdk-typing {
        0%, 80%, 100% {
          transform: scale(0.8);
          opacity: 0.5;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }

      /* Responsive */
      @media (max-width: 480px) {
        .modul-assistant-sdk-chat-widget {
          bottom: 20px;
          right: 20px;
        }

        .modul-assistant-sdk-chat-window {
          position: fixed !important;
          bottom: 20px;
          left: 20px;
          right: 20px;
          width: calc(100vw - 40px) !important;
          height: calc(100vh - 120px) !important;
          max-height: 600px;
          border-radius: 12px;
        }
      }
    `;
  }

  /**
   * 🎨 Obtiene los estilos específicos del tema
   * @private
   * @param {string} theme - Tema seleccionado
   * @returns {string} Estilos CSS específicos del tema
   */
  _getThemeSpecificStyles(theme) {
    switch (theme) {
      case "modern":
        return ThemeStyles.getModernThemeStyles();
      case "minimal":
        return ThemeStyles.getMinimalThemeStyles();
      case "dark":
        return ThemeStyles.getDarkThemeStyles();
      case "colorful":
        return ThemeStyles.getColorfulThemeStyles();
      case "elegant":
        return ThemeStyles.getElegantThemeStyles();
      case "professional":
      default:
        return this._getProfessionalThemeStyles();
    }
  }

  /**
   * 💼 Obtiene los estilos del tema profesional (por defecto)
   * @private
   * @returns {string} Estilos CSS del tema profesional
   */
  _getProfessionalThemeStyles() {
    return `
      /* Tema Profesional */
      .modul-assistant-sdk-chat-button {
        background: linear-gradient(135deg, #334155 0%, #475569 100%);
        box-shadow: 0 4px 20px rgba(51, 65, 85, 0.3);
      }

      .modul-assistant-sdk-chat-button:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 25px rgba(51, 65, 85, 0.4);
        background: linear-gradient(135deg, #475569 0%, #64748b 100%);
      }

      .modul-assistant-sdk-chat-button.active {
        background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        box-shadow: 0 4px 20px rgba(220, 38, 38, 0.3);
      }

      .modul-assistant-sdk-chat-window {
        box-shadow: 0 20px 50px rgba(30, 41, 59, 0.15);
        border: 1px solid #e2e8f0;
      }

      .modul-assistant-sdk-chat-header {
        background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
        border-bottom: 1px solid #e2e8f0;
      }

      .modul-assistant-sdk-chat-body {
        background: #f8fafc;
      }

      .modul-assistant-sdk-chat-message.bot {
        box-shadow: 0 2px 8px rgba(30, 41, 59, 0.08);
        border: 1px solid #e5e7eb;
      }

      /* Estilos para mensajes de error en tema profesional */
      .modul-assistant-sdk-chat-message.bot.error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #b91c1c;
        box-shadow: 0 2px 8px rgba(185, 28, 28, 0.08);
      }

      .modul-assistant-sdk-chat-message.bot.error-message strong {
        color: #991b1b;
        font-weight: bold;
      }

      .modul-assistant-sdk-chat-message.bot.error-message small {
        color: #64748b;
        font-style: italic;
        opacity: 0.8;
      }

      .modul-assistant-sdk-chat-message.bot a {
        color: #334155;
        text-decoration-color: #94a3b8;
      }

      .modul-assistant-sdk-chat-message.bot a:hover {
        color: #1e293b;
        text-decoration-color: #64748b;
      }

      .modul-assistant-sdk-chat-message.user {
        background: linear-gradient(135deg, #334155 0%, #475569 100%);
        box-shadow: 0 2px 8px rgba(51, 65, 85, 0.2);
      }

      .modul-assistant-sdk-chat-input-area {
        border-top: 1px solid #e5e7eb;
      }

      .modul-assistant-sdk-chat-input {
        border: 1px solid #d1d5db;
        background: #f9fafb;
      }

      .modul-assistant-sdk-chat-input:focus {
        border-color: #64748b;
        background: white;
        box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.1);
      }

      .modul-assistant-sdk-chat-send {
        background: linear-gradient(135deg, #334155 0%, #475569 100%);
      }

      .modul-assistant-sdk-chat-send:hover {
        background: linear-gradient(135deg, #475569 0%, #64748b 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(51, 65, 85, 0.2);
      }

      .modul-assistant-sdk-chat-option {
        border: 1px solid #d1d5db;
      }

      .modul-assistant-sdk-chat-option:hover {
        background: #f8fafc;
        border-color: #64748b;
        color: #1e293b;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(30, 41, 59, 0.08);
      }

      .modul-assistant-sdk-typing-indicator {
        box-shadow: 0 2px 8px rgba(30, 41, 59, 0.08);
        border: 1px solid #e5e7eb;
      }

      .modul-assistant-sdk-typing-dot {
        background: #94a3b8;
      }
    `;
  }

  /**
   * 💼 Obtiene los estilos del tema profesional (por defecto)
   * @private
   * @returns {string} Estilos CSS del tema profesional
   */
  _getProfessionalThemeStyles() {
    return `
      /* Tema Profesional */
      .modul-assistant-sdk-chat-button {
        background: linear-gradient(135deg, #334155 0%, #475569 100%);
        box-shadow: 0 4px 20px rgba(51, 65, 85, 0.3);
      }

      .modul-assistant-sdk-chat-button:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 25px rgba(51, 65, 85, 0.4);
        background: linear-gradient(135deg, #475569 0%, #64748b 100%);
      }

      .modul-assistant-sdk-chat-button.active {
        background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        box-shadow: 0 4px 20px rgba(220, 38, 38, 0.3);
      }

      .modul-assistant-sdk-chat-window {
        box-shadow: 0 20px 50px rgba(30, 41, 59, 0.15);
        border: 1px solid #e2e8f0;
      }

      .modul-assistant-sdk-chat-header {
        background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
        border-bottom: 1px solid #e2e8f0;
      }

      .modul-assistant-sdk-chat-body {
        background: #f8fafc;
      }

      .modul-assistant-sdk-chat-message.bot {
        box-shadow: 0 2px 8px rgba(30, 41, 59, 0.08);
        border: 1px solid #e5e7eb;
      }

      /* Estilos para mensajes de error en tema profesional */
      .modul-assistant-sdk-chat-message.bot.error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #b91c1c;
        box-shadow: 0 2px 8px rgba(185, 28, 28, 0.08);
      }

      .modul-assistant-sdk-chat-message.bot.error-message strong {
        color: #991b1b;
        font-weight: bold;
      }

      .modul-assistant-sdk-chat-message.bot.error-message small {
        color: #64748b;
        font-style: italic;
        opacity: 0.8;
      }

      .modul-assistant-sdk-chat-message.bot a {
        color: #334155;
        text-decoration-color: #94a3b8;
      }

      .modul-assistant-sdk-chat-message.bot a:hover {
        color: #1e293b;
        text-decoration-color: #64748b;
      }

      .modul-assistant-sdk-chat-message.user {
        background: linear-gradient(135deg, #334155 0%, #475569 100%);
        box-shadow: 0 2px 8px rgba(51, 65, 85, 0.2);
      }

      .modul-assistant-sdk-chat-input-area {
        border-top: 1px solid #e5e7eb;
      }

      .modul-assistant-sdk-chat-input {
        border: 1px solid #d1d5db;
        background: #f9fafb;
      }

      .modul-assistant-sdk-chat-input:focus {
        border-color: #64748b;
        background: white;
        box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.1);
      }

      .modul-assistant-sdk-chat-send {
        background: linear-gradient(135deg, #334155 0%, #475569 100%);
      }

      .modul-assistant-sdk-chat-send:hover {
        background: linear-gradient(135deg, #475569 0%, #64748b 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(51, 65, 85, 0.2);
      }

      .modul-assistant-sdk-chat-option {
        border: 1px solid #d1d5db;
      }

      .modul-assistant-sdk-chat-option:hover {
        background: #f8fafc;
        border-color: #64748b;
        color: #1e293b;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(30, 41, 59, 0.08);
      }

      .modul-assistant-sdk-typing-indicator {
        box-shadow: 0 2px 8px rgba(30, 41, 59, 0.08);
        border: 1px solid #e5e7eb;
      }

      .modul-assistant-sdk-typing-dot {
        background: #94a3b8;
      }
    `;
  }
}
